/**
 * tokens.ts — Canonical design tokens for Wipe v1.1 (Nokia 8-bit direction).
 *
 * Source of truth for color, spacing, typography, motion, shape, and elevation.
 * Consumed by ColorModeProvider.tsx to build the MUI theme.
 *
 * See .planning/phases/05-design-system/05-DESIGN-SYSTEM.md and 05-AESTHETIC.md.
 */

export const color = {
  // Monochrome ramp
  pixelBlack: '#0b0f0b',
  pixelGraphite: '#1f241f',
  pixelAsh: '#4a4f4a',
  pixelMuted: '#8a8f8a',
  pixelPaper: '#f9f7f0',
  pixelLime: '#cdd8c5',
  pixelCream: '#f4f0e8',

  // Accents (only three — don't add more without an ADR).
  // All hand-tuned for ≥ 4.5:1 contrast against pixelPaper / pixelLime in light
  // mode and ≥ 4.5:1 against pixelBlack / pixelGraphite in dark mode.
  walletAmber: '#7a5400',  // 5.7:1 on #f9f7f0 paper, 5.4:1 on #f1f1f3 trending bar
  deathRed: '#a02018',     // 7.1:1 on #f9f7f0 paper
  sparkGreen: '#2f5a1c',   // 6.9:1 on #f9f7f0 paper (was #5a8a3a, only 3.8:1)

  // Contrast-safe text
  textLight: '#0b0f0b',
  textLightMuted: '#525252', // WCAG AA ≥ 4.5:1 on pixelPaper
  textDark: '#f4f0e8',
  textDarkMuted: '#b8b8b8', // WCAG AA ≥ 4.5:1 on pixelBlack

  // Links
  linkLight: '#0a3d80',
  linkDark: '#7ab8f0',
} as const;

export const font = {
  display: '"VT323", "Nokia", monospace',
  body: '"Nokia FC22", "VT323", monospace',
} as const;

export const space = {
  tick: 4,
  step: 8,
  quarter: 16,
  half: 24,
  full: 48,
} as const;

export const radius = {
  flat: 0,
  pixel: 2,
  card: 4,
} as const;

export const border = {
  thin: '1px solid currentColor',
  arcade: '2px solid currentColor',
  dashed: '2px dashed currentColor',
} as const;

export const elevation = {
  e0: 'none',
  e1: '2px 2px 0 0 currentColor',
  e2: '4px 4px 0 0 currentColor',
} as const;

export const motion = {
  snap: '0ms steps(1, end)',
  tickTock: '600ms steps(6, end)',
  fadeIn: '120ms linear',
  arcadePress: '80ms steps(2, jump-none)',
} as const;

export const tokens = { color, font, space, radius, border, elevation, motion };
